import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { updateIncomeData } from '../../../actions/refiApplicationActions'
import { selectIncomeData } from '../../../reducers/refiApplication'

interface IncomeDetailsFormProps {
  vehicle?: any
  user?: any
}

const IncomeDetailsForm: React.FC<IncomeDetailsFormProps> = ({ vehicle, user }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const savedIncomeData = useSelector(selectIncomeData)
  
  const [formData, setFormData] = useState({
    currentEmployer: savedIncomeData.currentEmployer || '',
    employmentType: savedIncomeData.employmentType || '',
    annualIncome: savedIncomeData.annualIncome || '',
    startDateMonth: savedIncomeData.startDateMonth || '',
    startDateDay: savedIncomeData.startDateDay || '',
    startDateYear: savedIncomeData.startDateYear || ''
  })

  // Load saved data on mount
  useEffect(() => {
    if (savedIncomeData.currentEmployer || savedIncomeData.employmentType) {
      setFormData({
        currentEmployer: savedIncomeData.currentEmployer || '',
        employmentType: savedIncomeData.employmentType || '',
        annualIncome: savedIncomeData.annualIncome || '',
        startDateMonth: savedIncomeData.startDateMonth || '',
        startDateDay: savedIncomeData.startDateDay || '',
        startDateYear: savedIncomeData.startDateYear || ''
      })
    }
  }, [])
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [touched, setTouched] = useState<{[key: string]: boolean}>({})

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user makes selection
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Generate options for dropdowns
  const employmentTypes = [
    "AT HOME", "DISABILITY", "EMPLOYED", "EXECUTIVE", "LABOURER", "MANAGEMENT", "MILITARY", "OFFICE STAFF", "OTHER", "PRODUCTION", "PROFESSIONAL", "RETIRED", "RETIRED - MILITARY", "SALES", "SELF - EMPLOYED", "SIMI PROFESSIONAL", "SERVICE", "SOCIAL SECURITY", "STUDENT", "TRADES", "UNEMPLOYED"
  ]

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const days = Array.from({ length: 31 }, (_, i) => i + 1)
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 20 }, (_, i) => currentYear - i)

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {}

    if (!formData.currentEmployer.trim()) {
      newErrors.currentEmployer = 'Current employer is required'
    }

    if (!formData.employmentType) {
      newErrors.employmentType = 'Employment type is required'
    }

    if (!formData.annualIncome.trim()) {
      newErrors.annualIncome = 'Annual income is required'
    } else if (!/^\$?[\d,]+\.?\d*$/.test(formData.annualIncome.trim())) {
      newErrors.annualIncome = 'Please enter a valid amount (e.g., $65,250)'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    setTouched({
      currentEmployer: true,
      employmentType: true,
      annualIncome: true,
      startDateMonth: true,
      startDateDay: true,
      startDateYear: true
    })

    if (validateForm()) {
      dispatch(updateIncomeData(formData))
      history.push('/refiApp/financing/apply/consent')
    }
  }

  const handleBack = () => {
    history.push('/refiApp/financing/apply/residence-details')
  }

  return (
    <div className="income-details-form" style={{ maxWidth: '540px', margin: '0 auto', padding: '20px' }}>
      <div className="income-form">
        <div className="income-form__header">
          <h1 className="income-form__title">What Occupies your Day?</h1>
          <p className="income-form__description">
            {user?.firstName || 'Hi'}, proven employment and income are often times required to secure the best offers with our lending partners.
          </p>
        </div>

        <div className="income-form__fields">
          <div className="form__section">
            <label htmlFor="currentEmployer" className="form__label">
              Current Employer
            </label>
            <input
              id="currentEmployer"
              name="currentEmployer"
              type="text"
              value={formData.currentEmployer}
              onChange={handleInputChange}
              onBlur={() => setTouched(prev => ({ ...prev, currentEmployer: true }))}
              placeholder="example: Walmart"
              className={`form__input form__input--full-width ${errors.currentEmployer ? 'form__input--error' : ''}`}
            />
            {touched.currentEmployer && errors.currentEmployer && (
              <p className="form__help-text text-error" style={{ marginTop: '2px', marginBottom: '0' }}>{errors.currentEmployer}</p>
            )}
          </div>

          <div className="form__section">
            <label htmlFor="employmentType" className="form__label">
              Employment Type
            </label>
            <select
              id="employmentType"
              name="employmentType"
              value={formData.employmentType}
              onChange={handleSelectChange}
              onBlur={() => setTouched(prev => ({ ...prev, employmentType: true }))}
              className={`form__input form__input--full-width ${errors.employmentType ? 'form__input--error' : ''}`}
            >
              <option value="">Select employment type</option>
              {employmentTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {touched.employmentType && errors.employmentType && (
              <p className="form__help-text text-error" style={{ marginTop: '2px', marginBottom: '0' }}>{errors.employmentType}</p>
            )}
          </div>

          <div className="form__section">
            <label htmlFor="annualIncome" className="form__label">
              Annual Income
            </label>
            <input
              id="annualIncome"
              name="annualIncome"
              type="text"
              value={formData.annualIncome}
              onChange={handleInputChange}
              onBlur={() => setTouched(prev => ({ ...prev, annualIncome: true }))}
              placeholder="example: $65,250"
              className={`form__input form__input--full-width ${errors.annualIncome ? 'form__input--error' : ''}`}
            />
            {touched.annualIncome && errors.annualIncome && (
              <p className="form__help-text text-error" style={{ marginTop: '2px', marginBottom: '0' }}>{errors.annualIncome}</p>
            )}
          </div>

          <div className="form__section">
            <label className="form__label">Start Date (optional)</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="startDateMonth" className="form__label" style={{ fontSize: '14px', marginBottom: '5px' }}>
                  Month
                </label>
                <select
                  id="startDateMonth"
                  name="startDateMonth"
                  value={formData.startDateMonth}
                  onChange={handleSelectChange}
                  onBlur={() => setTouched(prev => ({ ...prev, startDateMonth: true }))}
                  className="form__input"
                  style={{ width: '100%' }}
                >
                  <option value="">MM</option>
                  {months.map((month, index) => (
                    <option key={month} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="startDateDay" className="form__label" style={{ fontSize: '14px', marginBottom: '5px' }}>
                  Day
                </label>
                <select
                  id="startDateDay"
                  name="startDateDay"
                  value={formData.startDateDay}
                  onChange={handleSelectChange}
                  onBlur={() => setTouched(prev => ({ ...prev, startDateDay: true }))}
                  className="form__input"
                  style={{ width: '100%' }}
                >
                  <option value="">DD</option>
                  {days.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="startDateYear" className="form__label" style={{ fontSize: '14px', marginBottom: '5px' }}>
                  Year
                </label>
                <select
                  id="startDateYear"
                  name="startDateYear"
                  value={formData.startDateYear}
                  onChange={handleSelectChange}
                  onBlur={() => setTouched(prev => ({ ...prev, startDateYear: true }))}
                  className="form__input"
                  style={{ width: '100%' }}
                >
                  <option value="">YYYY</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="income-form__actions" style={{ marginTop: '30px' }}>
          <button
            type="button"
            onClick={handleNext}
            className="button button--primary button--full-width"
          >
            Next
          </button>
        </div>

        <div className="income-form__navigation" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <button
            type="button"
            onClick={handleBack}
            className="button button--link-text"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  )
}

IncomeDetailsForm.displayName = 'Income Details Form'

export default IncomeDetailsForm
